import Dexie from 'dexie';
import TimeStamp from './TimeStamp';

class Accounts {
  constructor(name, typeId, taxId = '') {
    this.name = name;
    this.typeId = typeId;
    this.taxId = taxId; // Ajout du champ taxId
  }

  async save() {
    if (this.id) {
      await db.accounts.update(this.id, this);
    } else {
      this.id = await db.accounts.add(this);
    }
    return this;
  }

  static fromJS(obj) {
    const account = new Accounts(
      obj.name,
      obj.typeId,
      obj.taxId || '' // Ajout du taxId comme 3ème paramètre du constructeur
    );
    
    return account;
  }
}

class AIModels {
  constructor(apiKey, baseURL, maxtokens, model) {
    this.apiKey = apiKey;
    this.baseURL = baseURL;
    this.maxtokens = maxtokens;
    this.model = model;
  }

  async save() {
    if (this.id) {
      await db.aiModels.update(this.id, this);
    } else {
      this.id = await db.aiModels.add(this);
    }
    return this;
  }

  static fromJS(obj) {
    return new AIModels(
      obj.apiKey,
      obj.baseURL,
      obj.maxtokens,
      obj.model
    )
  }
}

class Contacts {
  constructor(firstName, lastName, email, phone, accountId, isPrimary = false, isBillingContact = false) {
    this.firstName = firstName;
    this.lastName = lastName;
    this.email = email;
    this.phone = phone;
    this.accountId = accountId;
    this.isPrimary = isPrimary;
    this.isBillingContact = isBillingContact;
  }

  async save() {
    if (this.id) {
      await db.contacts.update(this.id, this);
    } else {
      this.id = await db.contacts.add(this);
    }
    return this;
  }

  static fromJS(obj) {
    return new Contacts(
      obj.firstName,
      obj.lastName,
      obj.email,
      obj.phone,
      obj.accountId,
      obj.isPrimary,
      obj.isBillingContact
    );
  }
}

class Locations {
  constructor(address, accountId, isPrimary = false, postalCode = '', city = '', distanceKm = 0, isBillingLocation = false) {
    this.address = address;
    this.accountId = accountId;
    this.isPrimary = isPrimary;
    this.postalCode = postalCode;
    this.city = city;
    this.distanceKm = distanceKm;
    this.isBillingLocation = isBillingLocation;
  }

  async save() {
    if (this.id) {
      await db.locations.update(this.id, this);
    } else {
      this.id = await db.locations.add(this);
    }
    return this;
  }

  static fromJS(obj) {
    return new Locations(
      obj.address,
      obj.accountId,
      obj.isPrimary,
      obj.postalCode,
      obj.city,
      obj.distanceKm,
      obj.isBillingLocation
    );
  }
}

class Opportunities {
  constructor(name, accountId, timeStamp) {
    this.name = name;
    this.accountId = accountId;
    this.timeStamp = timeStamp instanceof TimeStamp ? timeStamp : TimeStamp.fromString(timeStamp);
  }

  async save() {
    if (this.id) {
      await db.opportunities.update(this.id, this);
    } else {
      this.id = await db.opportunities.add(this);
    }
    return this;
  }

  static fromJS(obj) {
    return new Opportunities(
      obj.name,
      obj.accountId,
      obj.timeStamp
    );
  }
}

class Quotes {
  constructor(quoteCustomId, opportunityId, timeStamp, status, name = '', isPrimary = false, notes = '', delay = '', outOfScope = '', needsFromClient = '') {
    this.quoteCustomId = quoteCustomId;
    this.opportunityId = opportunityId;
    this.timeStamp = timeStamp instanceof TimeStamp ? timeStamp : TimeStamp.fromString(timeStamp);
    this.status = status;
    this.name = name;
    this.isPrimary = isPrimary;
    this.notes = notes;
    this.delay = delay;
    this.outOfScope = outOfScope;
    this.needsFromClient = needsFromClient;
    this.paid = false;  // Nouvelle propriété initialisée à false
  }

  async save() {
    if (this.id) {
      await db.quotes.update(this.id, this);
    } else {
      this.id = await db.quotes.add(this);
    }
    return this;
  }

  // Calculer le total du devis à partir des lignes de devis hors options
  async getTotal() {

    // Récupérer le produit OPTIONLINE
    const optionProduct = await db.products.where('name').equals('OPTIONLINE').first();

    // Récupérer les lignes de devis
    const quoteLines = await db.quoteLines.where('quoteId').equals(this.id).toArray();

    // Récupérer les lignes parentes (hors options)
    const parentLines = quoteLines.filter(line => !line.parentLineId && parseInt(line.productId) !== parseInt(optionProduct.id));
    const parentLineIds = parentLines.map(line => line.id);

    // récupérer les sous-lignes qui n'ont pas une ligne parente de type OPTIONLINE
    const filteredLines = quoteLines.filter(line => {
      if (line.parentLineId) {
        return parentLineIds.includes(line.parentLineId);
      }
      return true;
    });

    const total = filteredLines.reduce((acc, line) => acc + (line.unitPrice * line.quantity), 0);
    return total;
  }


  static fromJS(obj) {
    const quote = new Quotes(
      obj.quoteCustomId,
      obj.opportunityId,
      obj.timeStamp,
      obj.status,
      obj.name,
      obj.isPrimary,
      obj.notes,
      obj.delay,
      obj.outOfScope,
      obj.needsFromClient
    );
    quote.paid = obj.paid || false;  // Assurer que paid est défini
    return quote;
  }
}

class QuoteLines {
  constructor(description, quoteId, parentLineId, unitPrice = 0, quantity = 0, productId = null, position = 0) {
    this.description = description;
    this.quoteId = quoteId;
    this.parentLineId = parentLineId;
    this.unitPrice = unitPrice;
    this.quantity = quantity;
    this.productId = productId;
    this.position = position;
  }

  async getFinalPrice() {
    if(this.parentLineId) {
      return this.unitPrice * this.quantity;
    } else {
      const subLines = await db.quoteLines.where('parentLineId').equals(this.id).toArray();
      return subLines.reduce((acc, line) => acc + (line.unitPrice * line.quantity), 0);
    }
  }


  async save() {
    if (this.id) {
      await db.quoteLines.update(this.id, this);
    } else {
      this.id = await db.quoteLines.add(this);
    }
    return this;
  }

  static fromJS(obj) {
    return new QuoteLines(
      obj.description,
      obj.quoteId,
      obj.parentLineId,
      obj.unitPrice,
      obj.quantity,
      obj.productId,
      obj.position
    );
  }
}

class Bills {
  constructor(billCustomId, quoteId, status, timeStamp, command = '', totalAmount = 0, alreadyPaid = 0, currentBillAmount = 0, currentProgressAmount = 0, isLastBill, dueDate = null) {
    this.billCustomId = billCustomId;
    this.quoteId = quoteId;
    this.status = status;
    this.timeStamp = timeStamp instanceof TimeStamp ? timeStamp : TimeStamp.fromString(timeStamp);
    this.command = command;
    this.totalAmount = totalAmount;
    this.alreadyPaid = alreadyPaid;
    this.currentBillAmount = currentBillAmount;
    this.currentProgressAmount = currentProgressAmount;
    this.isLastBill = isLastBill;
    this.dueDate = dueDate instanceof TimeStamp ? dueDate : (dueDate ? TimeStamp.fromString(dueDate) : null);
    
    // Champs EN16931
    this.sellerTaxID = '';               // Identifiant fiscal du vendeur
    this.sellerAddress = '';             // Adresse complète du vendeur
    this.customerTaxID = '';             // Identifiant fiscal du client
    this.customerBillingAddress = '';    // Adresse de facturation du client
    this.contractReference = '';         // Référence du contrat
    this.issueDate = null;               // Date d'émission de la facture
    this.paymentMethod = '';             // Mode de paiement
    this.paymentReference = '';          // Référence pour le paiement
    this.paymentTerms = '';              // Conditions de paiement
    this.vatAmount = 0;                  // Montant total de TVA
    this.vatRate = 0;                    // Taux de TVA principal
    this.currencyCode = 'EUR';           // Code de la devise
    this.invoiceType = 'COMMERCIAL';     // Type de facture
    this.paymentDueDate = null;          // Date d'échéance du paiement
    this.netAmount = 0;                  // Montant net (hors taxes)
  }

  async save() {
    if (this.id) {
      await db.bills.update(this.id, this);
    } else {
      this.id = await db.bills.add(this);
    }
    return this;
  }

  static fromJS(obj) {
    const bill = new Bills(
      obj.billCustomId,
      obj.quoteId,
      obj.status,
      obj.timeStamp,
      obj.command || '',
      obj.totalAmount || 0,
      obj.alreadyPaid || 0,
      obj.currentBillAmount || 0,
      obj.currentProgressAmount || 0,
      obj.isLastBill,
      obj.dueDate || null
    );
    
    // Ajouter les champs EN16931
    bill.sellerTaxID = obj.sellerTaxID || '';
    bill.sellerAddress = obj.sellerAddress || '';
    bill.customerTaxID = obj.customerTaxID || '';
    bill.customerBillingAddress = obj.customerBillingAddress || '';
    bill.contractReference = obj.contractReference || '';
    bill.issueDate = obj.issueDate ? (obj.issueDate instanceof TimeStamp ? obj.issueDate : TimeStamp.fromString(obj.issueDate)) : null;
    bill.paymentMethod = obj.paymentMethod || '';
    bill.paymentReference = obj.paymentReference || '';
    bill.paymentTerms = obj.paymentTerms || '';
    bill.vatAmount = obj.vatAmount || 0;
    bill.vatRate = obj.vatRate || 0;
    bill.currencyCode = obj.currencyCode || 'EUR';
    bill.invoiceType = obj.invoiceType || 'COMMERCIAL';
    bill.paymentDueDate = obj.paymentDueDate ? (obj.paymentDueDate instanceof TimeStamp ? obj.paymentDueDate : TimeStamp.fromString(obj.paymentDueDate)) : null;
    bill.netAmount = obj.netAmount || 0;
    
    return bill;
  }
}

class BillLines {
  constructor(description, billId, price, parentLineId, productId, progress = 0, lastProgess = 0) {
    this.description = description;
    this.billId = billId;
    this.price = price;
    this.parentLineId = parentLineId;
    this.productId = productId;
    this.progress = progress;
    this.lastProgess = lastProgess;
    
    // Champs EN16931
    this.itemIdentifier = '';        // Code d'identification de l'article (SKU)
    this.quantity = 1;               // Quantité d'articles
    this.unitOfMeasure = 'EA';       // Unité de mesure (EA=each/unité)
    this.unitPrice = 0;              // Prix unitaire
    this.lineVatRate = 0;            // Taux de TVA pour cette ligne
    this.lineVatAmount = 0;          // Montant de TVA pour cette ligne
    this.lineNetAmount = 0;          // Montant net de la ligne (hors taxes)
    this.lineTotalAmount = 0;        // Montant total de la ligne (avec taxes)
    this.vatCategory = 'S';          // Catégorie de TVA (S=standard, E=exempt, etc.)
    this.invoicePeriodStart = null;  // Début de période de facturation si applicable
    this.invoicePeriodEnd = null;    // Fin de période de facturation si applicable
  }

  async getCurrentProgressAmount() { 
    return this.price * this.progress / 100;
  }

  async save() {
    if (this.id) {
      await db.billLines.update(this.id, this);
    } else {
      this.id = await db.billLines.add(this);
    }
    return this;
  }

  static fromJS(obj) {
    const billLine = new BillLines(
      obj.description,
      obj.billId,
      obj.price,
      obj.parentLineId,
      obj.productId,
      obj.progress || 0,
      obj.lastProgess || 0
    );
    
    // Ajouter les champs EN16931
    billLine.itemIdentifier = obj.itemIdentifier || '';
    billLine.quantity = obj.quantity || 1;
    billLine.unitOfMeasure = obj.unitOfMeasure || 'EA';
    billLine.unitPrice = obj.unitPrice || 0;
    billLine.lineVatRate = obj.lineVatRate || 0;
    billLine.lineVatAmount = obj.lineVatAmount || 0;
    billLine.lineNetAmount = obj.lineNetAmount || 0;
    billLine.lineTotalAmount = obj.lineTotalAmount || 0;
    billLine.vatCategory = obj.vatCategory || 'S';
    billLine.invoicePeriodStart = obj.invoicePeriodStart ? (obj.invoicePeriodStart instanceof TimeStamp ? obj.invoicePeriodStart : TimeStamp.fromString(obj.invoicePeriodStart)) : null;
    billLine.invoicePeriodEnd = obj.invoicePeriodEnd ? (obj.invoicePeriodEnd instanceof TimeStamp ? obj.invoicePeriodEnd : TimeStamp.fromString(obj.invoicePeriodEnd)) : null;
    
    return billLine;
  }
}

class Settings {
  constructor(name, typeId, data = null, order = 0) {
    this.name = name;
    this.typeId = typeId;
    this.data = data;
    this.order = order;
  }

  async save() {
    if (this.id) {
      await db.settings.update(this.id, this);
    } else {
      this.id = await db.settings.add(this);
    }
    return this;
  }

  static fromJS(obj) {
    return new Settings(
      obj.name,
      obj.typeId,
      obj.data,
      obj.order
    );
  }
}

class SettingTypes {
  constructor(name, value) {
    this.name = name;
    this.value = value;
  }

  async save() {
    if (this.id) {
      await db.settingTypes.update(this.id, this);
    } else {
      this.id = await db.settingTypes.add(this);
    }
    return this;
  }

  static fromJS(obj) {
    return new SettingTypes(
      obj.name,
      obj.value
    );
  }
}

class AIPrompts {
  constructor(name, prompt) {
    this.name = name;
    this.prompt = prompt;
  }

  async save() {
    if (this.id) {
      await db.aiPrompts.update(this.id, this);
    } else {
      this.id = await db.aiPrompts.add(this);
    }
    return this;
  }

  static fromJS(obj) {
    return new AIPrompts(
      obj.name,
      obj.prompt
    );
  }
}

class Products {
  constructor(name, typeId, description, price, validFrom, untilTo, userProduct = false) {
    this.name = name;
    this.typeId = typeId;
    this.description = description;
    this.price = price;
    this.validFrom = validFrom instanceof TimeStamp ? validFrom : TimeStamp.fromString(validFrom);
    this.untilTo = untilTo instanceof TimeStamp ? untilTo : TimeStamp.fromString(untilTo);
    this.userProduct = userProduct;
  }

  async save() {
    if (this.id) {
      await db.products.update(this.id, this);
    } else {
      this.id = await db.products.add(this);
    }
    return this;
  }

  static fromJS(obj) {
    return new Products(
      obj.name,
      obj.typeId,
      obj.description,
      obj.price,
      obj.validFrom,
      obj.untilTo,
      obj.userProduct || false
    );
  }
}


export const db = new Dexie('Q&B');

db.version(4).stores({
  accounts: '++id, &name, typeId',
  aiModels: '++id',
  aiPrompts: '++id, name',
  products: '++id, name, typeId, &[name+validFrom+untilTo]',
  settings: '++id, name, typeId, &[name+typeId]',
  settingTypes: '++id, &name',
  contacts: '++id, &email, accountId, isPrimary',
  locations: '++id, accountId, [accountId+isPrimary]',  // Ajout de l'index composé ici
 opportunities: '++id, name, accountId, timeStamp, status',
  quotes: '++id, quoteCustomId, opportunityId, timeStamp, status, isPrimary',
  quoteLines: '++id, quoteId, productId, parentLineId',
  bills: '++id, billCustomId, quoteId, timeStamp, status',
  billLines: '++id, billId, parentLineId'
});

// Définition des tables
db.accounts.mapToClass(Accounts);
db.aiModels.mapToClass(AIModels);
db.contacts.mapToClass(Contacts);
db.locations.mapToClass(Locations);
db.opportunities.mapToClass(Opportunities);
db.quotes.mapToClass(Quotes);
db.quoteLines.mapToClass(QuoteLines);
db.bills.mapToClass(Bills);
db.billLines.mapToClass(BillLines);
db.settings.mapToClass(Settings);
db.settingTypes.mapToClass(SettingTypes);
db.aiPrompts.mapToClass(AIPrompts);
db.products.mapToClass(Products);

/**
 * Récupère le vendeur principal et son adresse principale
 * @returns {Promise<{account: Accounts, address: Locations|null}>}
 */
export async function getMainSellerWithAddress() {
  // 1. Chercher le paramètre Settings 'Principal'
  const principalSetting = await db.settings.where('name').equals('Principal').first();
  if (!principalSetting) throw new Error('Paramètre "Principal" non trouvé');

  // 2. Chercher l'account principal lié à ce paramètre
  const account = await db.accounts.where('typeId').equals(principalSetting.id).first();
  if (!account) throw new Error('Compte vendeur principal non trouvé');

  // 3. Chercher l'adresse principale liée à ce compte
  const address = await db.locations.where({ accountId: account.id, isPrimary: 1 }).first() ||
                  await db.locations.where({ accountId: account.id }).first() || null;

  return { account, address };
}

// Avant la définition de db, exporter toutes les classes nécessaires
export {
  Accounts,
  AIModels,
  Contacts,
  Locations,
  Opportunities,
  Quotes,
  QuoteLines,
  Bills,
  BillLines,
  Settings,
  SettingTypes,
  AIPrompts,
  Products
};

export default db;
